/**
 * What one product row shows and offers: the 5-dot completeness tracker, the status badge,
 * the draft-edits note and the actions in the ⋯ sheet / row menu. Pure, so cards, grid and
 * table all agree and the rules are tested once.
 */
import { getProcessForLegacyRole } from "../productProcessMigrationUtils";
import { isDraftStatus } from "../productStockDisplay";
import { canTransferProduct, canUndoBillingTransfer } from "../productOwnership";

export type DotState = "full" | "partial" | "none";
export type CompletenessDot = { key: string; label: string; state: DotState; text: string };

const BASIC_FIELDS = [
  "srNo", "typeOfSareeByDesign", "name", "colour", "brand", "weaver", "zari", "sareeWeight", "zariWeight",
  "purity", "yarnType", "loomNum", "xuid", "workingHours", "productionTimeInMonths", "silk", "handloom",
];

const stateOf = (info: boolean, media: boolean): DotState => (info && media ? "full" : info || media ? "partial" : "none");

const karigarText = (info: boolean, media: boolean) =>
  info && media
    ? "Karigar is entered and Media uploaded"
    : info
      ? "Karigar is entered and Media missing"
      : media
        ? "Karigar is missing, Media uploaded"
        : "Karigar and media missing";

/** Same five checks as the old status column: basic info, designing, dyeing, weaving, finishing. */
export function productCompleteness(product: any): CompletenessDot[] {
  const p = product || {};
  const basicInfo =
    BASIC_FIELDS.some((field) => p?.[field]?.length > 0) ||
    ["artform", "type", "category", "subcategory", "weavetype"].some((field) => p?.[field]?.name?.length > 0) ||
    p?.tags?.length > 0;
  const basicMedia = p?.thumbnailDetails?.media?.length > 0;
  const dots: CompletenessDot[] = [
    {
      key: "basic",
      label: "Basic information",
      state: stateOf(basicInfo, basicMedia),
      text:
        basicInfo && basicMedia
          ? "Basic Information is entered and Media uploaded"
          : basicInfo
            ? "Basic Information is entered and Media missing"
            : basicMedia
              ? "Basic Information is missing, Media uploaded"
              : "Basic Information and media missing",
    },
  ];
  ([
    ["designing", "Designing"],
    ["dying", "Dyeing"],
    ["weaving", "Weaving"],
    ["finishing", "Finishing"],
  ] as const).forEach(([role, label]) => {
    const process = getProcessForLegacyRole(p, role);
    const info = Boolean(process?.karigar?.name);
    const media = Boolean(process?.media?.length);
    dots.push({ key: role, label, state: stateOf(info, media), text: karigarText(info, media) });
  });
  return dots;
}

export type BadgeKind = "draft" | "edits" | "live" | "out" | "archived";

export function productBadge(product: any, draftRevisionsEnabled: boolean): { kind: BadgeKind; label: string } {
  if (isDraftStatus(product?.status)) return { kind: "draft", label: "Draft" };
  if (product?.archived === true) return { kind: "archived", label: "Archived" };
  if (draftRevisionsEnabled && product?.hasDraftChanges) return { kind: "edits", label: "Live · edits" };
  if (product?.inStock === false) return { kind: "out", label: "Out of stock" };
  return { kind: "live", label: "Live" };
}

export function hasUnpublishedEdits(product: any, draftRevisionsEnabled: boolean): boolean {
  return Boolean(draftRevisionsEnabled && product?.status === "COMPLETED" && product?.hasDraftChanges);
}

/** "2 edits saved as draft." when the list sends a count; the list does not always, so fall back to plain words. */
export function draftEditsLead(product: any): string {
  const raw = product?.draftChangeCount ?? product?.draftChangesCount ?? product?.draftEditCount;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return `${n} ${n === 1 ? "edit" : "edits"} saved as draft.`;
  return "Edits saved as draft.";
}

/** Menu item text: "Publish 2 draft edits" (or "Publish draft edits" when the list sends no count). */
export function publishEditsLabel(product: any): string {
  const raw = product?.draftChangeCount ?? product?.draftChangesCount ?? product?.draftEditCount;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return `Publish ${n} draft ${n === 1 ? "edit" : "edits"}`;
  return "Publish draft edits";
}

export type ProductActionKey =
  | "publish-draft"
  | "edit"
  | "view"
  | "preview"
  | "tag"
  | "add-qr"
  | "catalogue"
  | "transfer"
  | "undo-transfer"
  | "undo-billing"
  | "variant"
  | "archive"
  | "unarchive";

export type ProductAction = { key: ProductActionKey; label: string; hint?: string };
export type ProductActionGroup = { key: string; title: string; actions: ProductAction[] };

/**
 * Everything the old inline buttons + kebab offered, once each (Transfer used to be in both).
 * HKP tag (NFC) sits beside QR on drafts and live pieces alike (a tag goes on when weaving starts),
 * hidden once archived; undo-transfer only on live, unarchived pieces; QR when the piece has an ID;
 * Pre-View and Archive/Publish on live pieces; Publish Draft behind the flag.
 */
export function productActionGroups(product: any, ctx: { authUser: any; draftRevisionsEnabled: boolean }): ProductActionGroup[] {
  const p = product || {};
  const live = p.status === "COMPLETED";
  const liveActive = live && p.archived !== true;
  const tagQr: ProductAction[] = [];
  if (p.archived !== true) tagQr.push({ key: "tag", label: p?.xuid?.length > 0 ? "Edit HKP tag" : "Add HKP tag", hint: "NFC" });
  if (p.productUniqueId) tagQr.push({ key: "add-qr", label: "Add QR label" });
  const sell: ProductAction[] = [{ key: "catalogue", label: "Add to catalogue" }];
  if (canTransferProduct(p, ctx.authUser)) sell.push({ key: "transfer", label: "Transfer to buyer" });
  if (liveActive && p.transfer === true && p.transferTime != null) sell.push({ key: "undo-transfer", label: "Undo transfer" });
  if (canUndoBillingTransfer(p, ctx.authUser)) sell.push({ key: "undo-billing", label: "Undo transfer", hint: "take back" });
  sell.push({ key: "variant", label: "Make a colour variant" });
  const more: ProductAction[] = [];
  if (live) more.push({ key: "preview", label: "Preview certificate" });
  if (live) more.push(p.archived ? { key: "unarchive", label: "Publish", hint: "show in lists again" } : { key: "archive", label: "Archive", hint: "hides from lists" });
  return [
    { key: "tag-qr", title: "Tag & QR", actions: tagQr },
    { key: "sell", title: "Sell & share", actions: sell },
    { key: "more", title: "More", actions: more },
  ].filter((group) => group.actions.length > 0);
}

export function previewPath(product: any): string {
  const id = product?.productUniqueId;
  return product?.productOwner === "tantuvi by smriti morarka" ? `/pre-ViewProduct-tantuvi/${id}` : `/pre-viewProduct/${id}`;
}

export function formatRupee(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const n = Number(text.replace(/,/g, ""));
  if (!Number.isFinite(n)) return text;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const priceText = (value: unknown): string => (value == null ? "" : String(value));

/**
 * The price pair to send when one price field is saved. The list shows live prices, so the field
 * not being edited comes from the pending draft when a live piece already has one; otherwise a
 * second edit would send the live value back and wipe the first edit from the draft.
 */
export function priceSavePair(product: any, sellPrice: string, costPrice: string): { sellPrice: string; costPrice: string } {
  const liveSell = priceText(product?.sellPrice);
  const liveCost = priceText(product?.costPrice);
  const draft = product?.hasDraftChanges && product?.draft ? product.draft : null;
  const pendingSell = draft && "sellPrice" in draft ? priceText(draft.sellPrice) : liveSell;
  const pendingCost = draft && "costPrice" in draft ? priceText(draft.costPrice) : liveCost;
  return {
    sellPrice: sellPrice !== liveSell ? sellPrice : pendingSell,
    costPrice: costPrice !== liveCost ? costPrice : pendingCost,
  };
}
